// sendCheckAcquisitionCode.js
'use strict';
import debounce from 'lodash/debounce';

import fetch from '../resource/customFetch.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import handleFetchError from '../resource/handleFetchError.js';
import getMeData from '../selector/getMeData.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import addModal from './addModal.js';
import submitAcquisitionCode from './submitAcquisitionCode.js';
import resetAcquisitionCode from '../action/resetAcquisitionCode.js';

import {
  MERGE_OPERATION_DATA,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

const ERROR_CODE_PREFIX = 'acquisition_err_';
const ERROR_CODE_DEFAULT = 'error_code_not_valid';

let debouncedFunction;
const sendCheckAcquisitionCode = payload => async dispatch => {
  if (!debouncedFunction) {
    debouncedFunction = debounce(
      payload => dispatch(sendCheckAcquisitionCodeCore(payload)),
      1000 // TODO: remote config
    );
  }
  return debouncedFunction(payload);
};

/**
 * Send check acquisition code status
 * @kind action
 * @param {string} {code} - prepaid code
 * @param {boolean} {isAutoRedeemable} - is auto redeemable
 * @param {array} {acquisitionTypes} - expected acquisition types
 * @return {Promise} Action promise.
 */
export const sendCheckAcquisitionCodeCore =
  ({ code, isAutoRedeemable = true, acquisitionTypes = [] } = {}) =>
  async (dispatch, getState) => {
    if (!code) {
      return dispatch({ type: '' });
    }

    const selectPath = ['acquisition'];
    const networkingSelectPath = ['acquisition', code, 'check'];
    const token = getMeData(getState(), 'token');

    const fetchOptions = {
      method: 'GET',
      headers: {
        ...getHeaders(),
        Authorization: `Bearer ${token}`,
      },
    };

    const url = getResourceUrl({ endpoint: `/acquisition/${code}` });

    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });
    try {
      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }

      const location = response.headers.get('location');
      const { type, amount, reward } = await response.json();
      const isTypeMatched = acquisitionTypes.includes(type);
      if (!isTypeMatched) {
        throw new Error(JSON.stringify({ code: 'CODE_UNAVAILABLE' }));
      }
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath,
          data: {
            link: location,
            type,
            amount,
            reward,
            errorI18nkey: null,
          },
        },
      });
      if (type === 'referral-code') {
        if (isAutoRedeemable) {
          dispatch(submitAcquisitionCode({ code, type }));
        }
      } else if (type === 'prepaid-card' || type === 'order-claim') {
        dispatch(
          addModal({ id: 'PrepaidCodeConfirmModal', isHigherThanAll: true })
        );
      }

      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      let errorMessage = error.message;
      try {
        errorMessage = JSON.parse(error.message).code;
        // eslint-disable-next-line no-empty
      } catch (_) {}

      dispatch(resetAcquisitionCode());

      const errorI18nkey = errorMessage?.toLowerCase
        ? ERROR_CODE_PREFIX + errorMessage.toLowerCase()
        : ERROR_CODE_DEFAULT;

      // Display error message and incorrect code in input
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['acquisition'],
          data: {
            amount: null,
            errorI18nkey,
          },
        },
      });
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

export default sendCheckAcquisitionCode;
